import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import type { 
  ApprovalHistoryEntry, 
  ApprovalHistoryWithUsers, 
  ApprovalAction,
  ApprovalAttribution,
  ReviewStatus,
  PhotoReviewStatus
} from '@/types/gallery';

interface ApprovalHistoryFilters {
  entity_type?: 'gallery' | 'image';
  entity_id?: number;
  user_id?: string;
  action?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
}

export interface ApprovalHistoryHook {
  // History querying
  getApprovalHistory: (filters?: ApprovalHistoryFilters) => Promise<ApprovalHistoryWithUsers[]>;
  
  // Approval attribution
  getApprovalAttribution: (entityType: 'gallery' | 'image', entityId: number) => Promise<ApprovalAttribution | null>;
  
  // Action execution with attribution
  approveEntity: (entityType: 'gallery' | 'image', entityId: number, reason?: string) => Promise<boolean>;
  rejectEntity: (entityType: 'gallery' | 'image', entityId: number, reason: string) => Promise<boolean>;
  flagEntity: (entityType: 'gallery' | 'image', entityId: number, reason?: string) => Promise<boolean>;
  
  // Bulk operations
  bulkApprove: (actions: ApprovalAction[]) => Promise<boolean>;
  
  // State
  isLoading: boolean;
  error: string | null;
}

export const useApprovalHistory = (): ApprovalHistoryHook => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const getApprovalHistory = useCallback(async (
    filters: ApprovalHistoryFilters = {}
  ): Promise<ApprovalHistoryWithUsers[]> => {
    try {
      setIsLoading(true);
      setError(null);

      let query = supabase
        .from('audit_log')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply filters to match audit_log table structure
      if (filters.entity_type) {
        const tableName = filters.entity_type === 'gallery' ? 'galleries' : 'franchise_image_jobs';
        query = query.eq('table_name', tableName);
      }
      if (filters.entity_id) {
        query = query.eq('record_id', filters.entity_id.toString());
      }
      if (filters.user_id) {
        query = query.eq('user_id', filters.user_id);
      }
      if (filters.action) {
        query = query.eq('action', filters.action);
      }
      if (filters.date_from) {
        query = query.gte('created_at', filters.date_from);
      }
      if (filters.date_to) {
        query = query.lte('created_at', filters.date_to);
      }
      if (filters.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Failed to fetch approval history:', error);
        setError(error.message);
        return [];
      }

      setIsLoading(false);
      return data || [];
    } catch (error) {
      console.error('Error fetching approval history:', error);
      setError(error instanceof Error ? error.message : 'Unknown error');
      setIsLoading(false);
      return [];
    }
  }, []);

  const getApprovalAttribution = useCallback(async (
    entityType: 'gallery' | 'image',
    entityId: number
  ): Promise<ApprovalAttribution | null> => {
    try {
      const tableName = entityType === 'gallery' ? 'galleries' : 'franchise_image_jobs';
      
      // Get the most recent approval/rejection action for this entity
      const { data, error } = await supabase
        .from('audit_log')
        .select('*')
        .eq('table_name', tableName)
        .eq('record_id', entityId.toString())
        .in('action', ['approved', 'rejected'])
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) {
        console.error('Failed to fetch approval attribution:', error);
        return null;
      }

      if (!data || data.length === 0) {
        return null;
      }

      const auditEntry = data[0];
      const changes = auditEntry.changes as any;

      // For now, we don't have user details in audit_log, so we use user_id
      // In a real implementation, you might join with a users table
      return {
        approved_by: auditEntry.user_id,
        approved_at: auditEntry.created_at,
        approver_name: null, // Would need to fetch from users table
        approver_email: null, // Would need to fetch from users table
        rejection_reason: changes?.reason || null
      };
    } catch (error) {
      console.error('Error fetching approval attribution:', error);
      return null;
    }
  }, []);

  const updateEntityWithAttribution = useCallback(async (
    entityType: 'gallery' | 'image',
    entityId: number,
    newStatus: ReviewStatus | PhotoReviewStatus,
    reason?: string
  ): Promise<boolean> => {
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'You must be logged in to perform approval actions',
        variant: 'destructive'
      });
      return false;
    }

    try {
      const tableName = entityType === 'gallery' ? 'galleries' : 'franchise_image_jobs';
      // Update only the review_status field (approved_by, approved_at, rejection_reason don't exist)
      const updateData = {
        review_status: newStatus
      };

      console.log(`🔄 Updating ${entityType} ${entityId} status to ${newStatus}`, updateData);

      const { error } = await supabase
        .from(tableName)
        .update(updateData)
        .eq('id', entityId);

      if (error) {
        console.error(`Failed to update ${entityType}:`, error);
        toast({
          title: 'Update Failed',
          description: `Failed to update ${entityType}: ${error.message}`,
          variant: 'destructive'
        });
        return false;
      }

      // Log the approval action in audit_log for tracking attribution
      try {
        const auditData = {
          table_name: tableName,
          record_id: entityId.toString(),
          action: newStatus,
          user_id: user.id,
          changes: {
            review_status: newStatus,
            previous_status: 'pending',
            ...(reason && { reason })
          }
        };

        await supabase
          .from('audit_log')
          .insert(auditData);
      } catch (auditError) {
        // Don't fail the main operation if audit logging fails
        console.warn('Failed to log approval action:', auditError);
      }
      toast({
        title: 'Success',
        description: `${entityType} ${newStatus} successfully`,
        variant: 'default'
      });

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['approval-queue'] });
      queryClient.invalidateQueries({ queryKey: ['approval-history'] });
      queryClient.invalidateQueries({ queryKey: ['galleries'] });
      queryClient.invalidateQueries({ queryKey: ['images'] });

      return true;
    } catch (error) {
      console.error(`Error updating ${entityType}:`, error);
      toast({
        title: 'Error',
        description: `Failed to update ${entityType}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: 'destructive'
      });
      return false;
    }
  }, [user, toast, queryClient]);

  const approveEntity = useCallback(async (
    entityType: 'gallery' | 'image',
    entityId: number,
    reason?: string
  ): Promise<boolean> => {
    return updateEntityWithAttribution(entityType, entityId, 'approved', reason);
  }, [updateEntityWithAttribution]);

  const rejectEntity = useCallback(async (
    entityType: 'gallery' | 'image',
    entityId: number,
    reason: string
  ): Promise<boolean> => {
    return updateEntityWithAttribution(entityType, entityId, 'rejected', reason);
  }, [updateEntityWithAttribution]);

  const flagEntity = useCallback(async (
    entityType: 'gallery' | 'image',
    entityId: number,
    reason?: string
  ): Promise<boolean> => {
    const flagStatus = entityType === 'gallery' ? 'pending' : 'under_review';
    return updateEntityWithAttribution(entityType, entityId, flagStatus as any, reason);
  }, [updateEntityWithAttribution]);

  const bulkApprove = useCallback(async (actions: ApprovalAction[]): Promise<boolean> => {
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'You must be logged in to perform bulk approval actions',
        variant: 'destructive'
      });
      return false;
    }

    setIsLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const action of actions) {
        const status = action.type === 'approve' ? 'approved' : 
                      action.type === 'reject' ? 'rejected' : 'under_review';
        
        const success = await updateEntityWithAttribution(
          action.entity_type,
          action.entity_id,
          status as any,
          action.reason
        );

        if (success) {
          successCount++;
        } else {
          errorCount++;
        }
      }

      toast({
        title: 'Bulk Operation Complete',
        description: `${successCount} items processed successfully. ${errorCount} failed.`,
        variant: errorCount > 0 ? 'destructive' : 'default'
      });

      setIsLoading(false);
      return errorCount === 0;
    } catch (error) {
      console.error('Bulk approval operation failed:', error);
      toast({
        title: 'Bulk Operation Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      });
      setIsLoading(false);
      return false;
    }
  }, [user, toast, updateEntityWithAttribution]);

  return {
    getApprovalHistory,
    getApprovalAttribution,
    approveEntity,
    rejectEntity,
    flagEntity,
    bulkApprove,
    isLoading,
    error
  };
};

// Hook for querying approval history with React Query
export const useApprovalHistoryQuery = (filters?: ApprovalHistoryFilters) => {
  const { getApprovalHistory } = useApprovalHistory();

  return useQuery({
    queryKey: ['approval-history', filters],
    queryFn: () => getApprovalHistory(filters),
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false
  });
};

// Hook for getting approval attribution for a specific entity
export const useApprovalAttribution = (entityType: 'gallery' | 'image', entityId: number) => {
  return useApprovalHistoryQuery({
    entity_type: entityType,
    entity_id: entityId,
    limit: 10
  });
};